import api, { API_BASE } from './api';

export interface ProviderDefinition {
  code: string;
  label: string;
  beschreibung: string;
  sync_methoden: string[];
  browser_connector: boolean;
  manueller_upload: boolean;
  billing_url?: string | null;
  min_connector_version?: string | null;
  extension_id?: string | null;
  store_url?: string | null;
  firefox_store_url?: string | null;
  allowed_app_origins: string[];
}

export interface InvoiceOwner {
  id: number;
  vorname: string;
  nachname: string;
  email: string;
}

export interface AnbieterKonto {
  id: number;
  anbieter_code: string;
  anbieter_name?: string | null;
  bezeichnung: string;
  konto_email?: string | null;
  owner_id?: number | null;
  owner?: InvoiceOwner | null;
  aktiv: boolean;
  abrechnungsintervall: 'monatlich' | 'jaehrlich' | 'unregelmaessig';
  aktiv_ab: string;
  erwartet_bis_tag?: number | null;
  sync_methode: 'browser_connector' | 'manuell';
  sync_status: 'nie' | 'ok' | 'login_noetig' | 'fehler';
  letzter_sync_am?: string | null;
  letzter_erfolgreicher_sync_am?: string | null;
  letzter_fehler_code?: string | null;
  letzter_fehler_hinweis?: string | null;
  erstellt_am: string;
  aktualisiert_am: string;
  belege_anzahl: number;
  aktive_connectoren: number;
}

export interface AnbieterKontoInput {
  anbieter_code: string;
  anbieter_name?: string | null;
  bezeichnung: string;
  konto_email?: string | null;
  owner_id?: number | null;
  abrechnungsintervall?: string;
  aktiv_ab?: string;
  erwartet_bis_tag?: number | null;
  sync_methode?: string;
}

export interface Eingangsbeleg {
  id: number;
  anbieter_konto_id: number;
  anbieter_code: string;
  anbieter_bezeichnung: string;
  owner?: InvoiceOwner | null;
  dokument_typ: string;
  rechnungsnummer?: string | null;
  rechnungsdatum?: string | null;
  leistungsmonat?: string | null;
  nettobetrag?: number | null;
  steuerbetrag?: number | null;
  bruttobetrag?: number | null;
  waehrung?: string | null;
  bemerkung?: string | null;
  bearbeitungsstatus: 'neu' | 'klaerung' | 'verbucht' | 'archiviert';
  original_dateiname: string;
  dateigroesse: number;
  sha256: string;
  import_quelle: string;
  importiert_am: string;
  bearbeitet_am?: string | null;
  bearbeitet_von_id?: number | null;
}

export interface BelegListResponse {
  items: Eingangsbeleg[];
  total: number;
  page: number;
  size: number;
}

export interface UploadMetadata {
  dokument_typ?: string;
  rechnungsnummer?: string | null;
  rechnungsdatum?: string | null;
  leistungsmonat?: string | null;
  nettobetrag?: number | null;
  steuerbetrag?: number | null;
  bruttobetrag?: number | null;
  waehrung?: string | null;
  bemerkung?: string | null;
}

export interface ConnectorToken {
  id: number;
  geraet_name: string;
  connector_version?: string | null;
  erstellt_am: string;
  letzte_nutzung_am?: string | null;
  laeuft_ab_am?: string | null;
  widerrufen_am?: string | null;
}

export interface PairingCreated {
  id: number;
  pairing_ticket: string;
  laeuft_ab_am: string;
  api_origin: string;
  extension_id?: string | null;
  store_url?: string | null;
  firefox_store_url?: string | null;
}

export interface PairingStatus {
  id: number;
  status: 'offen' | 'eingeloest' | 'abgelaufen';
  laeuft_ab_am: string;
  eingeloest_am?: string | null;
  connector_token_id?: number | null;
}

export interface DuplicateCleanupItem {
  beleg_id: number;
  original_dateiname: string;
  import_quelle: string;
  rechnungsnummer?: string | null;
  bruttobetrag?: number | null;
  bearbeitungsstatus: string;
}

export interface DuplicateCleanupGroup {
  anbieter_konto_id: number;
  anbieter_bezeichnung: string;
  keeper_id: number;
  archive_ids: number[];
  items: DuplicateCleanupItem[];
}

export interface DuplicateCleanupPreview {
  gruppen: number;
  archivieren: number;
  groups: DuplicateCleanupGroup[];
}

export interface MonatskontoStatus {
  anbieter_konto_id: number;
  anbieter_code: string;
  bezeichnung: string;
  owner?: InvoiceOwner | null;
  status: string;
  belege_anzahl: number;
  letzter_sync_am?: string | null;
  letzter_fehler_hinweis?: string | null;
}

export interface Monatsuebersicht {
  monat: string;
  erwartet: number;
  vorhanden: number;
  geprueft_ohne_beleg: number;
  ausstehend: number;
  fehlend: number;
  login_noetig: number;
  fehler: number;
  neu: number;
  klaerung: number;
  verbucht: number;
  konten: MonatskontoStatus[];
}

export const eingangsbelegeService = {
  providers: async (): Promise<ProviderDefinition[]> =>
    (await api.get('/eingangsbelege/anbieter')).data,

  owners: async (): Promise<InvoiceOwner[]> =>
    (await api.get('/eingangsbelege/owners')).data,

  accounts: async (active?: boolean): Promise<AnbieterKonto[]> =>
    (await api.get('/eingangsbelege/konten', {
      params: active === undefined ? {} : { aktiv: active },
    })).data,

  createAccount: async (payload: AnbieterKontoInput): Promise<AnbieterKonto> =>
    (await api.post('/eingangsbelege/konten', payload)).data,

  updateAccount: async (
    id: number,
    payload: Partial<AnbieterKontoInput> & { aktiv?: boolean },
  ): Promise<AnbieterKonto> =>
    (await api.patch(`/eingangsbelege/konten/${id}`, payload)).data,

  deactivateAccount: async (id: number): Promise<AnbieterKonto> =>
    (await api.post(`/eingangsbelege/konten/${id}/deaktivieren`)).data,

  upload: async (
    accountId: number,
    files: File[],
    metadata: UploadMetadata,
  ): Promise<Array<{ beleg: Eingangsbeleg; duplicate: boolean }>> => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    form.append('metadata', JSON.stringify(metadata));
    return (await api.post(`/eingangsbelege/konten/${accountId}/upload`, form)).data;
  },

  documents: async (params: Record<string, string | number | undefined>): Promise<BelegListResponse> =>
    (await api.get('/eingangsbelege/belege', { params })).data,

  updateMetadata: async (
    id: number,
    payload: UploadMetadata,
  ): Promise<Eingangsbeleg> =>
    (await api.patch(`/eingangsbelege/belege/${id}/metadaten`, payload)).data,

  updateStatus: async (
    id: number,
    status: Eingangsbeleg['bearbeitungsstatus'],
    bemerkung?: string,
  ): Promise<Eingangsbeleg> =>
    (await api.patch(`/eingangsbelege/belege/${id}/status`, { status, bemerkung })).data,

  remove: async (id: number): Promise<void> => {
    await api.delete(`/eingangsbelege/belege/${id}`);
  },

  duplicatePreview: async (): Promise<DuplicateCleanupPreview> =>
    (await api.get('/eingangsbelege/duplikate')).data,

  cleanupDuplicates: async (): Promise<DuplicateCleanupPreview> =>
    (await api.post('/eingangsbelege/duplikate/bereinigen')).data,

  bulkStatus: async (
    ids: number[],
    status: Eingangsbeleg['bearbeitungsstatus'],
    bemerkung?: string,
  ): Promise<Eingangsbeleg[]> =>
    (await api.post('/eingangsbelege/belege/sammelstatus', {
      beleg_ids: ids,
      status,
      bemerkung,
    })).data,

  downloadUrl: (id: number): string => `${API_BASE}/eingangsbelege/belege/${id}/download`,

  exportZip: async (
    payload: {
      beleg_ids?: number[];
      leistungsmonat?: string;
      anbieter_code?: string;
      owner_id?: number;
      status?: string;
    },
  ): Promise<void> => {
    const response = await api.post('/eingangsbelege/export', payload, { responseType: 'blob' });
    const objectUrl = URL.createObjectURL(response.data);
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = match?.[1]
      || (payload.leistungsmonat
        ? `Eingangsbelege_${payload.leistungsmonat}.zip`
        : 'Eingangsbelege.zip');
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  },

  monthly: async (month: string): Promise<Monatsuebersicht> =>
    (await api.get(`/eingangsbelege/monatsuebersicht/${month}`)).data,

  connectorTokens: async (accountId: number): Promise<ConnectorToken[]> =>
    (await api.get(`/eingangsbelege/konten/${accountId}/connector-tokens`)).data,

  revokeConnectorToken: async (accountId: number, tokenId: number): Promise<void> => {
    await api.delete(`/eingangsbelege/konten/${accountId}/connector-tokens/${tokenId}`);
  },

  createPairing: async (accountId: number): Promise<PairingCreated> =>
    (await api.post(`/eingangsbelege/konten/${accountId}/connector-pairing`)).data,

  pairingStatus: async (accountId: number, pairingId: number): Promise<PairingStatus> =>
    (await api.get(`/eingangsbelege/konten/${accountId}/connector-pairing/${pairingId}`)).data,
};

export default eingangsbelegeService;
